import { darkTheme, lightTheme } from "../themes/Theme";

/**
 * Returns either black (lightTheme.textColorPrimary) or white (darkTheme.textColorPrimary) based on
 * the highest contrast with inputted hex code
 * @param {string} hexCode
 */
export const getTextColor = (hexCode: string): string => {
    let yiq;
    if (hexCode.length === 7) {
        // https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
        const r = parseInt(hexCode.substring(1, 3), 16);
        const g = parseInt(hexCode.substring(3, 5), 16);
        const b = parseInt(hexCode.substring(5, 7), 16);

        yiq = (r * 299 + g * 587 + b * 114) / 1000; // Get YIQ ratio
    }
    return yiq && yiq >= 133 ? lightTheme.textColorPrimary : darkTheme.textColorPrimary;
};

const hexToRgb = (hexCode: string): { r: number; g: number; b: number } => {
    if (hexCode.length === 7) {
        const r = parseInt(hexCode.substring(1, 3), 16);
        const g = parseInt(hexCode.substring(3, 5), 16);
        const b = parseInt(hexCode.substring(5, 7), 16);

        return { r, g, b };
    }
    return { r: 0, g: 0, b: 0 };
};

const rgbToHex = (r: number, g: number, b: number) => {
    const toHex = (c: number) => {
        const hex = c.toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Returns a new hex code that is a blend between two hex codes based on the ratio
 * @param hex1
 * @param hex2
 * @param ratio Ratio of the hex1 to use in the blend. Ex: if the ratio is 0.8, the new
 * hex code will use 80% of hex1 and 20% of hex2
 */
export const blendHexColors = (hex1: string, hex2: string, ratio: number) => {
    // if ratio is an invalid number, use 0.5
    ratio = ratio > 1 || ratio < 0 ? 0.5 : ratio;

    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);

    const blendedR = Math.round(rgb1.r * ratio + rgb2.r * (1 - ratio));
    const blendedG = Math.round(rgb1.g * ratio + rgb2.g * (1 - ratio));
    const blendedB = Math.round(rgb1.b * ratio + rgb2.b * (1 - ratio));

    return rgbToHex(blendedR, blendedG, blendedB);
};

/**
 * React's `style` prop type does not include CSS custom properties.
 * This utility is for casting CSS custom properties to be assignable to the `style` prop.
 *
 * @example
 * <div style={customProperties({"--background": "yellow"})} />
 */
export const customProperties = (properties: { [key: `--${string}`]: string | number }): React.CSSProperties =>
    properties;
